/**
 * Sort_by 商品置顶功能
 * 通过 URL 参数 Sort_by 指定商品 handle 并置顶
 * 用法：
 *   - 单个：?Sort_by=handle
 *   - 多个：?Sort_by=handle1,handle2
 *   - 数字 ID：?Sort_by=12345678
 *
 * 引用：
 *   assets/shopifyStorefront.js（可选，用于数字 ID 解析）
 * 关联：
 *   sections/main-collection-product-grid.liquid
 *   assets/facets.js
 *   sections/product-card.liquid
 * 发布同步：
 *   assets/main-collection-sort-by.js（本文件）
 *   assets/shopifyStorefront.js
 *   sections/product-card.liquid
 *   sections/main-collection-product-grid.liquid
 *   assets/facets.js
 */
class CollectionSortByPin {
  constructor(api) {
    this.api = api;
  }

  static isNumericId(value) {
    return /^\d+$/.test(value);
  }

  getProductGrid() {
    const el = document.getElementById('product-grid');
    return el && el.tagName === 'UL' ? el : null;
  }

  insertProductElement(productElement) {
    const productGrid = this.getProductGrid();
    if (!productGrid) {
      return false;
    }
    if (!productElement) return false;

    productGrid.insertBefore(productElement, productGrid.firstChild);

    if (typeof customElements !== 'undefined' && customElements.upgrade) {
      customElements.upgrade(productElement);
    }

    return true;
  }

  removeDuplicateProducts(pinnedHandles) {
    if (!pinnedHandles || pinnedHandles.length === 0) return;
    const productGrid = this.getProductGrid();
    if (!productGrid) return;

    const items = Array.from(productGrid.querySelectorAll(':scope > li'));
    const seenPinnedHandles = new Set();
    items.forEach((li) => {
      if (li.classList.contains('product-grid__item--pinned')) {
        const handle = li.getAttribute('data-product-handle');
        if (handle && pinnedHandles.includes(handle)) {
          if (seenPinnedHandles.has(handle)) {
            li.remove();
          } else {
            seenPinnedHandles.add(handle);
          }
        }
        return;
      }
      const link = li.querySelector('a[href*="/products/"]');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      const match = href.match(/\/products\/([^/?]+)/);
      const handle = match ? match[1] : null;
      if (handle && pinnedHandles.includes(handle)) {
        li.remove();
      }
    });
  }

  async fetchProductItemElement(handle) {
    if (!handle) return null;
    try {
      const response = await fetch(`/products/${handle}?section_id=product-card`, {
        credentials: 'same-origin',
      });

      if (!response.ok) {
        console.warn(`[Sort_by] 获取商品卡片失败: ${handle}`, response.status);
        return null;
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const productElement =
        doc.querySelector('.product-grid__item--pinned') ||
        doc.querySelector('li.grid__item') ||
        doc.querySelector('#shopify-section-product-card li');

      if (!productElement) {
        console.warn(`[Sort_by] 片段中未找到商品元素: ${handle}`);
        return null;
      }

      return document.importNode ? document.importNode(productElement, true) : productElement.cloneNode(true);
    } catch (error) {
      console.error(`[Sort_by] 获取商品卡片失败: ${handle}`, error);
      return null;
    }
  }

  async getProductByHandle(handle) {
    const productElement = await this.fetchProductItemElement(handle);
    if (!productElement) return null;
    return this.insertProductElement(productElement) ? productElement : null;
  }

  async run() {
    const SortByParam = new URLSearchParams(window.location.search).get('Sort_by');
    if (!SortByParam) return;

    const items = SortByParam.split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .reverse();

    const pinnedHandles = [];
    for (const item of items) {
      let handle = item;
      if (CollectionSortByPin.isNumericId(item)) {
        const resolved = await this.resolveHandleById(item);
        if (resolved) handle = resolved;
        else continue;
      }
      const inserted = await this.getProductByHandle(handle);
      if (inserted) pinnedHandles.push(handle);
    }

    this.removeDuplicateProducts(pinnedHandles);
  }

  async resolveHandleById(numericId) {
    if (!this.api || !this.api.shopifyFetch) return null;
    try {
      const gid = `gid://shopify/Product/${numericId}`;
      const result = await this.api.shopifyFetch(
        `query ($id: ID!) { product(id: $id) { handle } }`,
        { id: gid },
        { useCache: true }
      );
      return result?.product?.handle || null;
    } catch {
      return null;
    }
  }
}

function runCollectionSortByPin() {
  const api = window.ShopifyStorefrontAPI || null;
  const instance = new CollectionSortByPin(api);
  instance.run();
}

document.addEventListener('DOMContentLoaded', () => {
  if (window.ShopifyStorefrontAPI?.onInit) {
    window.ShopifyStorefrontAPI.onInit(runCollectionSortByPin);
  } else {
    document.addEventListener('ShopifyStorefrontAPI:init', (e) => {
      runCollectionSortByPin();
    });
  }

  if (window.ShopifyStorefrontAPI) {
    runCollectionSortByPin();
  }
});

window.CollectionSortByPin = { run: runCollectionSortByPin };
